/**
 * Kalkulator przyrostu masy w ciąży — logika obliczeń i skrypt bloku.
 *
 * Jeden IIFE, bez opakowania UMD: motyw buduje każdy plik bloku przez
 * webpack (laravel-mix), który każdemu modułowi podstawia własny obiekt
 * `module` — więc sprawdzenie „typeof module === 'object'” z UMD zawsze
 * wygrywało i gałąź `root.PregnancyWeightGainCalc = factory()` nigdy się
 * nie wykonywała. Druga część skryptu (obsługa DOM, niżej) czytała więc
 * `calc = null` i cicho przerywała `init()`, zanim spięła choćby jeden
 * nasłuch na polach - stąd kalkulator nie reagował na wpisywanie ani suwaki.
 *
 * `calc` jest zwykłą zmienną w tym samym zamknięciu co kod DOM, bez
 * przechodzenia przez obiekt globalny. Wzory, progi i zakresy pochodzą
 * z briefu (sekcje 3.1–3.4); wartości liczbowe siedzą w konfiguracji,
 * nie w kodzie. Musi dawać te same liczby co metody klasy PHP bloku
 * (tabela B07, tabelka stanu A, wypełnienie suwaka).
 */
(function () {
	'use strict';

	var NBSP = '\u00a0'; // spacja nierozdzielająca, jak number_format() po stronie PHP

	/**
	 * Liczba z pola tekstowego: przecinek jako separator dziesiętny, spacje ucinane.
	 * Puste / nieliczbowe → NaN.
	 */
	function toNumber(value) {
		if (value === null || typeof value === 'undefined') {
			return NaN;
		}
		var raw = String(value).replace(/\s/g, '').replace(',', '.');
		if (raw === '' || !/^-?\d*(\.\d+)?$/.test(raw) || raw === '-' || raw === '.') {
			return NaN;
		}
		return parseFloat(raw);
	}

	/**
	 * Zaokrąglenie do 0,1 kg (brief 3.3), symetryczne względem zera.
	 * Przed zaokrągleniem ucinamy śmieci zmiennoprzecinkowe (64,9 − 60,5 to
	 * w IEEE 754 4,399999…), tak jak robi to wzorzec spalania przed floor().
	 */
	function round1(value) {
		var sign = value < 0 ? -1 : 1;
		var abs = Math.round(Math.abs(value) * 1e6) / 1e6;
		return sign * (Math.round(abs * 10 + 1e-9) / 10);
	}

	/**
	 * Liczba w polskim zapisie: przecinek dziesiętny, zawsze `decimals` miejsc.
	 * Minus jako prawdziwy znak minus (U+2212).
	 */
	function format(value, decimals) {
		var fixed = Math.abs(value).toFixed(decimals).replace('.', ',');
		return (value < 0 ? '−' : '') + fixed;
	}

	/**
	 * Zapis jak w briefie dla zakresów całkowitych: „11,5" i „16" (bez „,0").
	 */
	function formatTrim(value) {
		return format(value, 1).replace(/,0$/, '');
	}

	/**
	 * Zakres „a–b": półpauza bez spacji, jak w tabelach kalkulatora spalania.
	 */
	function formatRange(range, trim) {
		var f = trim ? formatTrim : function (v) { return format(v, 1); };
		return f(range.lower) + '–' + f(range.upper);
	}

	/**
	 * Odczyt pola: { value, empty, invalid }.
	 */
	function readField(raw, limit) {
		var text = raw === null || typeof raw === 'undefined' ? '' : String(raw).trim();
		if (text === '') {
			return { value: null, empty: true, invalid: false };
		}
		var value = toNumber(text);
		if (isNaN(value) || value < limit.min || value > limit.max) {
			return { value: null, empty: false, invalid: true };
		}
		return { value: value, empty: false, invalid: false };
	}

	/**
	 * Pozycja wartości na torze suwaka, w procentach (odpowiednik fill_percent() w PHP).
	 */
	function fillPercent(value, min, max) {
		if (!(max > min)) {
			return 0;
		}
		return Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100));
	}

	// --- Wzory z briefu ------------------------------------------------------

	/**
	 * BMI_przed = masa / (wzrost_m)^2 — brief 3.1.
	 * Liczone jako masa × 10000 / wzrost_cm², a wynik obcinany do 9 miejsc: inaczej
	 * 64 kg / 1,60 m daje w IEEE 754 24,999999999999996 zamiast 25,0 i próg nadwagi
	 * (brief: „25,0 – 29,9") przepuszcza wynik do masy prawidłowej.
	 */
	function bmi(massKg, heightCm) {
		return Math.round((massKg * 10000 / (heightCm * heightCm)) * 1e9) / 1e9;
	}

	/** Kategoria BMI — brief 3.1, progi z konfiguracji. */
	function category(bmiValue, config) {
		var thresholds = config.bmi.thresholds;
		for (var i = 0; i < thresholds.length; i++) {
			if (thresholds[i].below === null || bmiValue < thresholds[i].below) {
				return thresholds[i].key;
			}
		}
		return thresholds[thresholds.length - 1].key;
	}

	function categoryLabel(key, config) {
		for (var i = 0; i < config.categories.length; i++) {
			if (config.categories[i].key === key) {
				return config.categories[i].label;
			}
		}
		return '';
	}

	/** Zakres całkowity — brief 3.2. null = brak danych (bliźniacza + niedowaga). */
	function totalRange(categoryKey, twins, config) {
		var set = twins ? config.totals.twins : config.totals.single;
		var pair = set[categoryKey];
		if (!pair) {
			return null;
		}
		return { lower: pair[0], upper: pair[1] };
	}

	/**
	 * Zakres dla tygodnia — brief 3.3, zaokrąglony do 0,1 kg.
	 * Do 13. tygodnia wspólny przedział; potem interpolacja liniowa przez 27 tygodni.
	 */
	function weekRange(total, week, config) {
		var t1 = config.trimester1;
		var lower;
		var upper;

		if (week <= t1.last_week) {
			lower = t1.lower;
			upper = t1.upper;
		} else {
			var rateLower = (total.lower - t1.lower) / t1.span_weeks;
			var rateUpper = (total.upper - t1.upper) / t1.span_weeks;
			lower = t1.lower + (week - t1.last_week) * rateLower;
			upper = t1.upper + (week - t1.last_week) * rateUpper;
		}

		return { lower: round1(lower), upper: round1(upper) };
	}

	/**
	 * Klasyfikacja — brief 3.4. Granice włącznie, porównanie z zakresem
	 * zaokrąglonym (tym, który widzi użytkowniczka). Ujemny przyrost osobno.
	 *
	 * Tygodnie 1–13 (decyzja po weryfikacji z IOM 2009): zakres 0,5–2,0 kg
	 * dotyczy końca 13. tygodnia, więc "poniżej zakresu" i spadek masy nie są
	 * oceniane — dostają neutralny status 'trimester1'. "Powyżej zakresu" zostaje.
	 */
	function classify(gain, range, trimester1) {
		if (trimester1 && gain < range.lower) {
			return 'trimester1';
		}
		if (gain < 0) {
			return 'negative';
		}
		if (gain < range.lower) {
			return 'below';
		}
		if (gain > range.upper) {
			return 'above';
		}
		return 'in';
	}

	// --- Pełny wynik dla panelu ---------------------------------------------

	/**
	 * @param {object} input  { massBefore, height, week, massNow, twins } — surowe wartości pól.
	 * @param {object} config Konfiguracja (src/config.json).
	 * @param {object} [options] { fallbackWeek } — ostatni poprawny tydzień, gdy pole jest puste/błędne.
	 *
	 * Stany panelu:
	 *  'empty'    – masa sprzed ciąży lub wzrost nie podane / błędne (stan A),
	 *  'no-range' – bliźniacza + niedowaga (komunikat B05/5),
	 *  'range'    – zakres bez porównania (masa dzisiaj pusta / błędna),
	 *  'result'   – pełny wynik.
	 */
	function compute(input, config, options) {
		options = options || {};
		var limits = config.limits;
		var twins = !!input.twins;

		var fields = {
			massBefore: readField(input.massBefore, limits.mass_before),
			height: readField(input.height, limits.height),
			week: readField(input.week, limits.week),
			massNow: readField(input.massNow, limits.mass_now)
		};

		var weekValue = fields.week.value;
		if (weekValue === null) {
			weekValue = typeof options.fallbackWeek === 'number' ? options.fallbackWeek : limits.week['default'];
		}

		var errors = [];
		['massBefore', 'height', 'week', 'massNow'].forEach(function (key) {
			if (fields[key].invalid) {
				errors.push(key);
			}
		});
		// Tydzień bez wartości traktujemy jak błąd pola — tak jak czas we wzorcu.
		if (fields.week.empty) {
			errors.push('week');
		}

		var result = {
			state: 'empty',
			fields: fields,
			errors: errors,
			twins: twins,
			week: weekValue,
			trimester1: weekValue <= config.trimester1.last_week,
			bmi: null,
			category: null,
			categoryLabel: '',
			total: null,
			range: null,
			gain: null,
			status: null,
			messages: []
		};

		if (fields.massBefore.value !== null && fields.height.value !== null) {
			result.bmi = bmi(fields.massBefore.value, fields.height.value);
			result.category = category(result.bmi, config);
			result.categoryLabel = categoryLabel(result.category, config);
			result.total = totalRange(result.category, twins, config);

			if (result.total === null) {
				result.state = 'no-range';
			} else {
				result.range = weekRange(result.total, weekValue, config);
				if (fields.massNow.value !== null) {
					result.state = 'result';
					result.gain = round1(fields.massNow.value - fields.massBefore.value);
					result.status = classify(result.gain, result.range, result.trimester1);
				} else {
					result.state = 'range';
				}
			}
		}

		// Komunikaty B05 w kolejności z briefu: 1 poniżej, 2 powyżej, 3 ujemny,
		// 4 pierwszy trymestr, 5 bliźniacza + niedowaga, 6 stały.
		if (result.status === 'below') {
			result.messages.push('below');
		}
		if (result.status === 'above') {
			result.messages.push('above');
		}
		if (result.status === 'negative') {
			result.messages.push('negative');
		}
		if (result.trimester1) {
			result.messages.push('trimester1');
		}
		if (result.state === 'no-range') {
			result.messages.push('twins-underweight');
		}
		result.messages.push('always');

		return result;
	}

	// --- Dane renderowane po stronie serwera --------------------------------

	/** Tabelka stanu A (brief 5.1): cztery kategorie × tydzień z konfiguracji. */
	function sampleRows(config) {
		return config.categories.map(function (cat) {
			var total = totalRange(cat.key, config.sample.twins, config);
			var range = weekRange(total, config.sample.week, config);
			return {
				key: cat.key,
				label: cat.label,
				bmi_label: cat.bmi_label,
				range: range,
				range_display: formatRange(range, false) + NBSP + 'kg'
			};
		});
	}

	/** Tabela B07 (brief 11): wiersz „do 13" + tygodnie z konfiguracji, ciąża pojedyncza. */
	function tableRows(config) {
		var t1 = config.trimester1;
		var rows = [];

		function row(week, label, weekFrom, weekTo) {
			var cells = config.categories.map(function (cat) {
				var total = totalRange(cat.key, false, config);
				var range = weekRange(total, week, config);
				return { key: cat.key, label: cat.label, range: range, display: formatRange(range, false) + NBSP + 'kg' };
			});
			return { label: label, week_from: weekFrom, week_to: weekTo, cells: cells };
		}

		rows.push(row(t1.last_week, config.table.first_row_label, config.limits.week.min, t1.last_week));
		config.table.weeks.forEach(function (week, index) {
			var next = config.table.weeks[index + 1];
			// Wiersz obejmuje swój tydzień i tygodnie do następnego wiersza (t21 → wiersz 20).
			var to = typeof next === 'number' ? next - 1 : config.limits.week.max;
			rows.push(row(week, String(week), week, to));
		});

		return rows;
	}

	/**
	 * Tabela B07 w grupach trymestrów (akordeon jak grupy aktywności we wzorcu).
	 * Wiersz trafia do grupy, w której leży jego tydzień; grupa zna zakres tygodni,
	 * a otwarta na starcie jest ta z tygodniem domyślnym.
	 */
	function tableGroups(config) {
		var rows = tableRows(config);
		var defaultWeek = config.limits.week['default'];
		return config.table.groups.map(function (group) {
			return {
				key: group.key,
				label: group.label,
				week_from: group.from,
				week_to: group.to,
				open: defaultWeek >= group.from && defaultWeek <= group.to,
				rows: rows.filter(function (row) {
					return row.week_from >= group.from && row.week_from <= group.to;
				})
			};
		});
	}

	var calc = {
		toNumber: toNumber,
		round1: round1,
		format: format,
		formatTrim: formatTrim,
		formatRange: formatRange,
		readField: readField,
		fillPercent: fillPercent,
		bmi: bmi,
		category: category,
		categoryLabel: categoryLabel,
		totalRange: totalRange,
		weekRange: weekRange,
		classify: classify,
		compute: compute,
		sampleRows: sampleRows,
		tableRows: tableRows,
		tableGroups: tableGroups
	};

	// --- Kalkulator przyrostu masy w ciąży - skrypt bloku (część DOM) ------
	//
	// Skrypt niczego nie dorenderowuje: tabelka przykładowa, tabela B07 i wszystkie
	// teksty stoją w źródle HTML. Tutaj dzieje się tylko przeliczanie na bieżąco,
	// przełączanie stanów panelu, rysowanie wykresu pasma (inline SVG), podświetlenie
	// wiersza tabeli i otwarcie grupy trymestru, przyklejany pasek nawigacji i scrollspy.
	//
	// Konfiguracja przychodzi atrybutem data-config. Copy jest w szablonie
	// (atrybuty data-*), skrypt podstawia tylko liczby.
	//
	// PRYWATNOŚĆ: wartości wpisane przez użytkowniczkę żyją wyłącznie w pamięci
	// strony. Nic nie trafia do adresu, localStorage/sessionStorage, cookies,
	// dataLayer, atrybutów data-* ani do konsoli.
	//
	// Vanilla JS, bez jQuery.

	var SVG_NS = 'http://www.w3.org/2000/svg';

	function toggle(element, visible) {
		if (element) {
			element.hidden = !visible;
		}
	}

	function setText(element, text) {
		if (element) {
			element.textContent = text;
		}
	}

	function fill(template, values) {
		return String(template || '').replace(/\{(\w+)\}/g, function (match, key) {
			return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match;
		});
	}

	function svgEl(name, attrs, text) {
		var el = document.createElementNS(SVG_NS, name);
		Object.keys(attrs || {}).forEach(function (key) {
			el.setAttribute(key, attrs[key]);
		});
		if (typeof text === 'string') {
			el.textContent = text;
		}
		return el;
	}

	function initCalculator(root, config) {
		var el = function (role) { return root.querySelector('[data-role="' + role + '"]'); };

		var inputs = {
			massBefore: { number: el('mass-before-number'), range: el('mass-before-range'), fill: el('mass-before-fill'), error: el('mass-before-error'), limit: config.limits.mass_before, unit: 'kg' },
			height: { number: el('height-number'), range: el('height-range'), fill: el('height-fill'), error: el('height-error'), limit: config.limits.height, unit: 'cm' },
			week: { number: el('week-number'), range: el('week-range'), fill: el('week-fill'), error: el('week-error'), limit: config.limits.week, unit: '' },
			massNow: { number: el('mass-now-number'), range: el('mass-now-range'), fill: el('mass-now-fill'), error: el('mass-now-error'), limit: config.limits.mass_now, unit: 'kg' }
		};
		var twinsInputs = Array.prototype.slice.call(root.querySelectorAll('[data-role="twins"]'));
		var twinsHint = el('twins-hint');

		var emptyState = root.querySelector('[data-state="empty"]');
		var valueState = root.querySelector('[data-state="value"]');
		var lead = el('result-lead');
		var figure = el('figure');
		var figureValue = el('figure-value');
		var gainLine = el('gain-line');
		var gainEl = el('gain');
		var statusEl = el('status');
		var chartWrap = el('chart-wrap');
		var chart = el('chart');
		var chartDesc = el('chart-desc');
		var caption = el('caption');
		var totalLine = el('total-line');
		var totalEl = el('total');
		var liveEl = el('live');
		var tableRows = Array.prototype.slice.call(root.querySelectorAll('[data-role="table-row"]'));
		var tableGroups = Array.prototype.slice.call(root.querySelectorAll('[data-role="table-group"]'));

		if (!inputs.massBefore.number || !inputs.height.number || !inputs.week.number || !inputs.massNow.number || !emptyState || !valueState) {
			return;
		}

		// Ostatni poprawny tydzień - panel nie może zgubić wartości, gdy pole
		// zostanie skasowane albo dostanie liczbę spoza zakresu.
		var lastWeek = calc.toNumber(inputs.week.number.value);
		if (isNaN(lastWeek)) {
			lastWeek = config.limits.week['default'];
		}
		var openedGroupWeek = lastWeek;

		function isTwins() {
			return twinsInputs.some(function (input) {
				return input.checked && input.value === 'twins';
			});
		}

		/**
		 * Suwak i pole liczbowe to jedna wartość w dwóch kontrolkach. Pole puste
		 * = gałka szara, tor pusty (nic nie sugeruje wartości).
		 */
		function syncSlider(field, state, fallback) {
			var value = state.value;
			var shown = value !== null ? value : fallback;

			if (field.range) {
				field.range.classList.toggle('is-empty', shown === null);
				if (shown !== null) {
					field.range.value = Math.min(field.limit.max, Math.max(field.limit.min, shown));
					field.range.setAttribute('aria-valuetext', calc.format(shown, field.limit.step < 1 ? 1 : 0) + (field.unit ? ' ' + field.unit : ''));
				} else {
					field.range.setAttribute('aria-valuetext', 'nie podano');
				}
			}
			if (field.fill) {
				field.fill.style.width = shown === null ? '0%' : calc.fillPercent(shown, field.limit.min, field.limit.max) + '%';
			}
		}

		// --- Wykres pasma ---------------------------------------------------

		function drawChart(result) {
			if (!chart) {
				return;
			}
			while (chart.firstChild) {
				chart.removeChild(chart.firstChild);
			}

			var W = 360;
			var H = 180;
			var padL = 34;
			var padR = 12;
			var padT = 24;
			var padB = 26;
			var plotW = W - padL - padR;
			var plotH = H - padT - padB;
			var weekMin = config.limits.week.min;
			var weekMax = config.limits.week.max;

			var rawTop = result.total.upper;
			var rawBottom = 0;
			if (result.gain !== null) {
				rawTop = Math.max(rawTop, result.gain);
				rawBottom = Math.min(0, result.gain);
			}
			rawTop += 1;

			// Dobiera "ładny" krok osi (1/2/5 * 10^n), tak by zmieścić się w max 8 wartościach.
			var maxTicks = 8;
			var rawRange = Math.max(rawTop - rawBottom, 1);
			var roughStep = rawRange / (maxTicks - 1);
			var magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
			var niceSteps = [1, 2, 5, 10];
			var step = niceSteps[niceSteps.length - 1] * magnitude;
			for (var i = 0; i < niceSteps.length; i++) {
				if (roughStep <= niceSteps[i] * magnitude) {
					step = niceSteps[i] * magnitude;
					break;
				}
			}

			var yTop = Math.ceil(rawTop / step) * step;
			var yBottom = Math.floor(rawBottom / step) * step;

			function x(week) { return padL + ((week - weekMin) / (weekMax - weekMin)) * plotW; }
			function y(kg) { return padT + ((yTop - kg) / (yTop - yBottom)) * plotH; }

			var stepDecimals = Math.max(0, -Math.floor(Math.log10(step)));
			for (var kg = yBottom; kg <= yTop + step / 2; kg += step) {
				var kgRounded = Math.round(kg * Math.pow(10, stepDecimals)) / Math.pow(10, stepDecimals);
				chart.appendChild(svgEl('line', { 'class': kgRounded === 0 ? 'pwg-axis' : 'pwg-grid', x1: padL, x2: W - padR, y1: y(kgRounded), y2: y(kgRounded) }));
				chart.appendChild(svgEl('text', { 'class': 'pwg-tick', x: padL - 6, y: y(kgRounded) + 4, 'text-anchor': 'end' }, String(kgRounded)));
			}
			chart.appendChild(svgEl('text', { 'class': 'pwg-tick', x: padL - 6, y: padT - 14, 'text-anchor': 'end' }, 'kg'));

			[config.trimester1.last_week, 20, 30, 40].forEach(function (week) {
				chart.appendChild(svgEl('line', { 'class': 'pwg-grid', x1: x(week), x2: x(week), y1: padT, y2: padT + plotH }));
				chart.appendChild(svgEl('text', { 'class': 'pwg-tick', x: x(week), y: H - 8, 'text-anchor': 'middle' }, String(week)));
			});
			chart.appendChild(svgEl('text', { 'class': 'pwg-tick', x: padL, y: H - 8, 'text-anchor': 'start' }, 'tydz.'));

			var upper = [];
			var lower = [];
			for (var week = weekMin; week <= weekMax; week++) {
				var r = calc.weekRange(result.total, week, config);
				upper.push(x(week).toFixed(1) + ',' + y(r.upper).toFixed(1));
				lower.unshift(x(week).toFixed(1) + ',' + y(r.lower).toFixed(1));
			}
			chart.appendChild(svgEl('polygon', { 'class': 'pwg-band', points: upper.concat(lower).join(' ') }));

			chart.appendChild(svgEl('line', { 'class': 'pwg-week-line', x1: x(result.week), x2: x(result.week), y1: padT, y2: padT + plotH }));
			if (result.gain !== null) {
				var py = Math.min(padT + plotH, Math.max(padT, y(result.gain)));
				chart.appendChild(svgEl('circle', { 'class': 'pwg-point' + (result.status === 'in' ? ' is-in' : ''), cx: x(result.week), cy: py, r: 6 }));
			}
		}

		function describeChart(result) {
			if (!chartWrap) {
				return '';
			}
			var values = {
				kategoria: result.categoryLabel,
				tydzien: result.week,
				dolny: calc.format(result.range.lower, 1),
				gorny: calc.format(result.range.upper, 1),
				calkowity: calc.formatRange(result.total, true),
				przyrost: result.gain !== null ? calc.format(result.gain, 1) : ''
			};
			var key = result.gain === null ? 'none' : (result.status === 'in' ? 'in' : (result.status === 'negative' || result.status === 'trimester1' ? 'negative' : 'out'));
			return fill(chartWrap.getAttribute('data-desc-' + key), values);
		}

		// --- Tabela B07: wiersz bieżącego tygodnia i grupa trymestru ------------

		function highlightTable(result) {
			tableRows.forEach(function (row) {
				var from = calc.toNumber(row.getAttribute('data-week-from'));
				var to = calc.toNumber(row.getAttribute('data-week-to'));
				row.classList.toggle('is-current', result.week >= from && result.week <= to);
			});
			Array.prototype.forEach.call(root.querySelectorAll('[data-role="table-row"] td[data-category]'), function (cell) {
				cell.classList.toggle('is-user', !result.twins && result.category !== null && cell.getAttribute('data-category') === result.category);
			});

			// Grupa z bieżącym tygodniem otwiera się, gdy tydzień się zmieni;
			// ręczne zwijanie przez użytkowniczkę nie jest nadpisywane bez zmiany tygodnia.
			if (result.week !== openedGroupWeek) {
				openedGroupWeek = result.week;
				tableGroups.forEach(function (group) {
					var from = calc.toNumber(group.getAttribute('data-week-from'));
					var to = calc.toNumber(group.getAttribute('data-week-to'));
					group.open = result.week >= from && result.week <= to;
				});
			}
		}

		function updateMessages(result) {
			Array.prototype.forEach.call(root.querySelectorAll('[data-message]'), function (element) {
				var key = element.getAttribute('data-message');
				toggle(element, result.messages.indexOf(key) !== -1);
			});
		}

		function setFigureClass(name) {
			figure.className = figure.className.replace(/\s?is-(in|gain)/g, '') + (name ? ' ' + name : '');
		}

		// --- Render -------------------------------------------------------------

		function render() {
			var result = calc.compute({
				massBefore: inputs.massBefore.number.value,
				height: inputs.height.number.value,
				week: inputs.week.number.value,
				massNow: inputs.massNow.number.value,
				twins: isTwins()
			}, config, { fallbackWeek: lastWeek });

			if (result.fields.week.value !== null) {
				lastWeek = result.fields.week.value;
			}

			Object.keys(inputs).forEach(function (key) {
				toggle(inputs[key].error, result.errors.indexOf(key) !== -1);
			});
			syncSlider(inputs.massBefore, result.fields.massBefore, null);
			syncSlider(inputs.height, result.fields.height, null);
			syncSlider(inputs.week, result.fields.week, lastWeek);
			syncSlider(inputs.massNow, result.fields.massNow, null);
			toggle(twinsHint, result.twins);

			var showValue = result.state === 'result' || result.state === 'range' || (result.state === 'no-range' && result.fields.massNow.value !== null);
			var live = '';

			if (showValue) {
				var week = result.week;

				if (result.state === 'no-range') {
					// Bliźniacza + niedowaga z podaną masą dzisiaj: sam przyrost, bez zakresu (komunikat B05/5).
					var gainOnly = calc.round1(result.fields.massNow.value - result.fields.massBefore.value);
					setText(lead, fill(lead.getAttribute('data-lead-gain'), { tydzien: week }));
					setText(figureValue, calc.format(gainOnly, 1));
					setFigureClass('is-gain');
					toggle(gainLine, false);
					toggle(chartWrap, false);
					toggle(caption, false);
					toggle(totalLine, false);
					live = 'Przyrost ' + calc.format(gainOnly, 1) + ' kg. Zakres nie jest liczony.';
				} else {
					// Zakres dla tygodnia jako liczba główna; przyrost i status pod nią.
					setText(lead, fill(lead.getAttribute('data-lead-range'), { tydzien: week }));
					setText(figureValue, calc.formatRange(result.range, false));
					setFigureClass('');
					setText(totalEl, calc.formatRange(result.total, true));
					toggle(totalLine, true);
					toggle(chartWrap, true);
					drawChart(result);
					setText(chartDesc, describeChart(result));

					if (result.state === 'result') {
						setText(gainEl, calc.format(result.gain, 1));
						// Status 'trimester1' (tygodnie 1–13, przyrost poniżej zakresu lub spadek) nie ma słowa statusu.
						var statusWord = gainLine.getAttribute('data-status-' + result.status) || '';
						setText(statusEl, statusWord ? ', ' + statusWord : '');
						statusEl.classList.toggle('is-in', result.status === 'in');
						toggle(gainLine, true);

						var text;
						if (result.status === 'in') {
							text = caption.getAttribute('data-caption-in');
						} else if (result.status === 'negative') {
							text = caption.getAttribute('data-caption-negative');
						} else if (result.status === 'trimester1') {
							text = caption.getAttribute('data-caption-trimester1');
						} else {
							var word = result.status === 'above' ? caption.getAttribute('data-word-above') : caption.getAttribute('data-word-below');
							text = fill(caption.getAttribute('data-caption-out'), { porownanie: word });
						}
						setText(caption, text);
						toggle(caption, true);
						live = 'Zakres w tym tygodniu ' + calc.formatRange(result.range, false) + ' kg. Przyrost ' + calc.format(result.gain, 1) + ' kg' + statusEl.textContent + '. ' + text;
					} else {
						toggle(gainLine, false);
						toggle(caption, false);
						live = 'Zakres w tym tygodniu ' + calc.formatRange(result.range, false) + ' kg.';
					}
				}
			}

			toggle(emptyState, !showValue);
			toggle(valueState, showValue);
			updateMessages(result);
			highlightTable(result);
			setText(liveEl, live);
		}

		Object.keys(inputs).forEach(function (key) {
			var field = inputs[key];
			field.number.addEventListener('input', render);
			if (field.range) {
				field.range.addEventListener('input', function () {
					field.number.value = String(field.range.value).replace('.', ',');
					render();
				});
			}
		});
		twinsInputs.forEach(function (input) {
			input.addEventListener('change', render);
		});

		render();
	}

	/**
	 * Nawigacja kotwicowa: pozycję stałą przełącza skrypt (jak we wzorcu),
	 * bo position: sticky kończy się razem z sekcją bloku.
	 */
	function initStickyNav(root) {
		var nav = root.querySelector('[data-role="nav"]');
		if (!nav) {
			return;
		}

		var header = document.querySelector('.site-header');
		var spacer = document.createElement('div');
		spacer.className = 'pregnancy-weight-gain-calculator__nav-spacer';
		spacer.hidden = true;
		nav.parentNode.insertBefore(spacer, nav);

		var isFixed = false;
		var ticking = false;

		var list = nav.querySelector('ul');
		var links = Array.prototype.slice.call(nav.querySelectorAll('a[href^="#"]'));
		var targets = links.map(function (link) {
			var id = link.getAttribute('href').slice(1);
			return id ? document.getElementById(id) : null;
		});
		var activeIndex = -1;

		function headerOffset() {
			if (!header) {
				return 0;
			}
			var position = window.getComputedStyle(header).position;
			if (position !== 'sticky' && position !== 'fixed') {
				return 0;
			}
			var rect = header.getBoundingClientRect();
			return rect.top <= 0 ? Math.max(0, Math.round(rect.bottom)) : 0;
		}

		function keepVisible(link) {
			if (!list || list.scrollWidth <= list.clientWidth) {
				return;
			}
			var item = link.parentNode.getBoundingClientRect();
			var box = list.getBoundingClientRect();
			var margin = 16;

			if (item.left < box.left + margin) {
				list.scrollLeft += item.left - box.left - margin;
			} else if (item.right > box.right - margin) {
				list.scrollLeft += item.right - box.right + margin;
			}
		}

		/**
		 * Aktywna jest ostatnia sekcja, której górna krawędź minęła linię odczytu.
		 * Różnice względem wzorca spalania (celowe, żeby pigułka odpowiadała
		 * sekcji, którą faktycznie widać):
		 *  - linia odczytu leży 35 % wysokości widocznego obszaru pod paskiem,
		 *    a nie tuż pod nim - sekcja zapala się, gdy zajmuje większość ekranu,
		 *  - gdy dwie sekcje zaczynają się na tej samej wysokości (na desktopie
		 *    pola i panel wyniku stoją obok siebie), wygrywa pierwsza z nich.
		 * Na samym dole strony wygrywa sekcja ostatnia.
		 */
		function updateActive(offset) {
			if (!links.length) {
				return;
			}

			var barBottom = offset + (isFixed ? nav.getBoundingClientRect().height : 0);
			var line = barBottom + Math.max(16, (window.innerHeight - barBottom) * 0.35);
			var current = -1;
			var currentTop = -Infinity;

			targets.forEach(function (target, index) {
				if (!target) {
					return;
				}
				var top = target.getBoundingClientRect().top;
				if (top <= line && top > currentTop + 2) {
					current = index;
					currentTop = top;
				}
			});

			var atBottom = window.innerHeight + window.pageYOffset >= document.documentElement.scrollHeight - 2;
			if (atBottom) {
				for (var i = targets.length - 1; i >= 0; i--) {
					if (targets[i]) {
						current = i;
						break;
					}
				}
			}

			if (current === activeIndex) {
				return;
			}

			if (links[activeIndex]) {
				links[activeIndex].classList.remove('is-active');
				links[activeIndex].removeAttribute('aria-current');
			}

			activeIndex = current;

			if (links[activeIndex]) {
				links[activeIndex].classList.add('is-active');
				links[activeIndex].setAttribute('aria-current', 'true');
				keepVisible(links[activeIndex]);
			}
		}

		function update() {
			ticking = false;

			var offset = headerOffset();
			root.style.setProperty('--pwg-sticky-top', offset + 'px');

			var top = (isFixed ? spacer : nav).getBoundingClientRect().top;

			if (!isFixed && top <= offset) {
				var margin = parseFloat(window.getComputedStyle(nav).marginBottom) || 0;
				spacer.style.height = nav.getBoundingClientRect().height + margin + 'px';
				spacer.hidden = false;
				nav.classList.add('is-fixed');
				isFixed = true;
			} else if (isFixed && top > offset) {
				nav.classList.remove('is-fixed');
				spacer.hidden = true;
				isFixed = false;
			}

			updateActive(offset);
		}

		function onScroll() {
			if (!ticking) {
				ticking = true;
				window.requestAnimationFrame(update);
			}
		}

		window.addEventListener('scroll', onScroll, { passive: true });
		window.addEventListener('resize', onScroll);
		window.addEventListener('load', onScroll);

		update();
	}

	function init() {
		var blocks = Array.prototype.slice.call(document.querySelectorAll('[data-pregnancy-weight-gain]'));

		blocks.forEach(function (root) {
			var config;
			try {
				config = JSON.parse(root.getAttribute('data-config') || '');
			} catch (error) {
				return;
			}
			initCalculator(root, config);
			initStickyNav(root);
		});
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}
})();
